import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trophy, Timer, Infinity, Volume2, VolumeX, Play } from 'lucide-react';
import { motion } from 'framer-motion';

const GRID_SIZE = 5;
const GAME_DURATION = 60; // 60 seconds

const WhackALight = () => {
  const navigate = useNavigate();
  const [lights, setLights] = useState([]);
  const [score, setScore] = useState(0);
  const [activeLight, setActiveLight] = useState(null);
  const [bestTime, setBestTime] = useState(null);
  const [lastReactionTime, setLastReactionTime] = useState(null);
  const [lightOnTime, setLightOnTime] = useState(null);
  const [gameMode, setGameMode] = useState(null); // 'timed' or 'endless' or null (menu)
  const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
  const [isGameOver, setIsGameOver] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [highScore, setHighScore] = useState(() => {
    const saved = localStorage.getItem('whackALightHighScore');
    return saved ? parseInt(saved) : 0;
  });
  
  const audioContextRef = useRef(null);
  const timerRef = useRef(null);
  const lightTimeoutRef = useRef(null);
  const isGameOverRef = useRef(isGameOver);
  const scoreRef = useRef(score);
  const highScoreRef = useRef(highScore);

  // Keep refs in sync with the latest state so timers/intervals never read stale values
  useEffect(() => {
    isGameOverRef.current = isGameOver;
  }, [isGameOver]);

  useEffect(() => {
    scoreRef.current = score;
  }, [score]);

  useEffect(() => {
    highScoreRef.current = highScore;
  }, [highScore]);

  // Initialize audio context
  const initAudio = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
  }, []);

  // Play thud sound
  const playThudSound = useCallback(() => {
    if (!soundEnabled || !audioContextRef.current) return;
    
    try {
      const ctx = audioContextRef.current;
      
      // Create a satisfying "thud" sound
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(150, ctx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(50, ctx.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.5, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.15);
      
      // Add a "punch" layer
      const noiseBuffer = ctx.createBuffer(1, ctx.sampleRate * 0.1, ctx.sampleRate);
      const noiseData = noiseBuffer.getChannelData(0);
      for (let i = 0; i < noiseBuffer.length; i++) {
        noiseData[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ctx.sampleRate * 0.02));
      }
      
      const noiseSource = ctx.createBufferSource();
      const noiseGain = ctx.createGain();
      noiseSource.buffer = noiseBuffer;
      noiseGain.gain.setValueAtTime(0.3, ctx.currentTime);
      noiseGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
      
      noiseSource.connect(noiseGain);
      noiseGain.connect(ctx.destination);
      noiseSource.start(ctx.currentTime);
    } catch (e) {
      console.log('Audio error:', e);
    }
  }, [soundEnabled]);

  // Initialize lights grid
  const initLights = useCallback(() => {
    const newLights = Array.from({ length: GRID_SIZE * GRID_SIZE }, (_, i) => ({
      id: i,
      active: false
    }));
    setLights(newLights);
  }, []);

  // Activate random light
  const activateRandomLight = useCallback(() => {
    if (isGameOverRef.current) return;

    const randomId = Math.floor(Math.random() * (GRID_SIZE * GRID_SIZE));
    setActiveLight(randomId);
    setLightOnTime(Date.now());
    setLights(prev => prev.map(l => ({ ...l, active: l.id === randomId })));

    // Auto-move to next light after 3 seconds if not tapped
    lightTimeoutRef.current = setTimeout(() => {
      if (!isGameOverRef.current) {
        activateRandomLight();
      }
    }, 3000);
  }, []);

  // Start game
  const startGame = (mode) => {
    initAudio();
    setGameMode(mode);
    setScore(0);
    setIsGameOver(false);
    setLastReactionTime(null);
    setBestTime(null);
    initLights();
    
    if (mode === 'timed') {
      setTimeLeft(GAME_DURATION);
    }
    
    setTimeout(() => activateRandomLight(), 500);
  };

  // Timer countdown for timed mode
  useEffect(() => {
    if (gameMode === 'timed' && !isGameOver && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            endGame();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(timerRef.current);
    }
  }, [gameMode, isGameOver]);

  // End game
  const endGame = () => {
    setIsGameOver(true);
    clearTimeout(lightTimeoutRef.current);
    clearInterval(timerRef.current);
    setLights(prev => prev.map(l => ({ ...l, active: false })));

    // Update high score (read the latest score/highScore via refs so a stale
    // interval closure never compares against an outdated value)
    const finalScore = scoreRef.current;
    if (finalScore > highScoreRef.current) {
      setHighScore(finalScore);
      localStorage.setItem('whackALightHighScore', finalScore.toString());
    }
  };

  // Handle light tap
  const handleLightTap = (id) => {
    if (id === activeLight && !isGameOver) {
      clearTimeout(lightTimeoutRef.current);
      
      const reactionTime = Date.now() - lightOnTime;
      setLastReactionTime(reactionTime);
      
      if (!bestTime || reactionTime < bestTime) {
        setBestTime(reactionTime);
      }
      
      setScore(prev => prev + 1);
      playThudSound();
      
      setLights(prev => prev.map(l => ({ ...l, active: false })));

      // Store this timeout in lightTimeoutRef (instead of leaving it untracked)
      // so endGame()/backToMenu()/unmount cleanup can always cancel it.
      lightTimeoutRef.current = setTimeout(() => {
        if (!isGameOverRef.current) {
          activateRandomLight();
        }
      }, 300);
    }
  };

  // Back to menu
  const backToMenu = () => {
    clearTimeout(lightTimeoutRef.current);
    clearInterval(timerRef.current);
    setGameMode(null);
    setIsGameOver(false);
    setScore(0);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimeout(lightTimeoutRef.current);
      clearInterval(timerRef.current);
      if (audioContextRef.current) {
        try {
          audioContextRef.current.close();
        } catch (e) {
          // Context may already be closed/closing; nothing further to do.
        }
        audioContextRef.current = null;
      }
    };
  }, []);

  // Initial setup
  useEffect(() => {
    initLights();
  }, [initLights]);

  // Game Menu Screen
  if (!gameMode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] to-[#16213e] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-green-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-2xl mx-auto px-6 py-12">
          <header className="flex justify-between items-center mb-12">
            <button
              data-testid="back-to-dashboard"
              onClick={() => navigate('/dashboard')}
              className="glass-effect text-white px-6 py-3 rounded-full font-semibold hover:bg-white/20 transition-all duration-300 flex items-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </button>
            
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="glass-effect text-white px-4 py-3 rounded-full hover:bg-white/20 transition-all"
              title={soundEnabled ? 'Mute sounds' : 'Enable sounds'}
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
          </header>

          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-white mb-4">Whack-a-Light</h1>
            <p className="text-white/70 text-lg">Test your reflexes! Tap the glowing lights as fast as you can.</p>
          </div>

          <div className="space-y-6">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => startGame('timed')}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-6 rounded-2xl font-bold text-xl shadow-lg flex items-center justify-center gap-3"
            >
              <Timer className="w-6 h-6" />
              Play for 60 Seconds
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => startGame('endless')}
              className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 text-white py-6 rounded-2xl font-bold text-xl shadow-lg flex items-center justify-center gap-3"
            >
              <Infinity className="w-6 h-6" />
              Play Without Timer
            </motion.button>
          </div>

          {highScore > 0 && (
            <div className="mt-12 glass-effect rounded-2xl p-6 text-center">
              <p className="text-white/60 mb-2">High Score (60s mode)</p>
              <p className="text-4xl font-bold text-yellow-400 flex items-center justify-center gap-2">
                <Trophy className="w-8 h-8" />
                {highScore}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Game Over Screen
  if (isGameOver) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] to-[#16213e] relative overflow-hidden flex items-center justify-center">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-green-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="glass-effect rounded-3xl p-12"
          >
            <h2 className="text-4xl font-bold text-white mb-6">Time's Up!</h2>
            
            <div className="space-y-4 mb-8">
              <div>
                <p className="text-white/60">Final Score</p>
                <p className="text-6xl font-bold text-green-400">{score}</p>
              </div>
              
              {score >= highScore && score > 0 && (
                <p className="text-yellow-400 font-bold text-xl">🎉 New High Score!</p>
              )}
              
              {bestTime && (
                <div>
                  <p className="text-white/60">Best Reaction Time</p>
                  <p className="text-2xl font-bold text-white">{bestTime}ms</p>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <button
                onClick={() => startGame('timed')}
                className="w-full bg-green-500 hover:bg-green-600 text-white py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2"
              >
                <Play className="w-5 h-5" />
                Play Again
              </button>
              
              <button
                onClick={backToMenu}
                className="w-full bg-white/10 hover:bg-white/20 text-white py-4 rounded-xl font-bold text-lg"
              >
                Back to Menu
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Game Play Screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] to-[#16213e] relative overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-green-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-8">
        <header className="flex justify-between items-center mb-8">
          <button
            onClick={backToMenu}
            className="glass-effect text-white px-6 py-3 rounded-full font-semibold hover:bg-white/20 transition-all duration-300 flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Menu
          </button>
          
          <h1 className="text-4xl font-bold text-white">Whack-a-Light</h1>
          
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="glass-effect text-white px-4 py-3 rounded-full hover:bg-white/20 transition-all"
            title={soundEnabled ? 'Mute sounds' : 'Enable sounds'}
          >
            {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
        </header>

        <div className="flex justify-center gap-8 mb-8">
          {gameMode === 'timed' && (
            <div className="glass-effect px-6 py-3 rounded-2xl text-center">
              <p className="text-white/60 text-sm mb-1">Time Left</p>
              <p className={`text-3xl font-bold ${timeLeft <= 10 ? 'text-red-400' : 'text-white'}`}>
                {timeLeft}s
              </p>
            </div>
          )}
          
          <div className="glass-effect px-6 py-3 rounded-2xl text-center">
            <p className="text-white/60 text-sm mb-1">Score</p>
            <p className="text-3xl font-bold text-yellow-400 flex items-center gap-2">
              <Trophy className="w-6 h-6" />
              {score}
            </p>
          </div>
          
          <div className="glass-effect px-6 py-3 rounded-2xl text-center">
            <p className="text-white/60 text-sm mb-1">Last Reaction</p>
            <p className="text-2xl font-bold text-white">
              {lastReactionTime ? `${lastReactionTime}ms` : '-'}
            </p>
          </div>
          
          <div className="glass-effect px-6 py-3 rounded-2xl text-center">
            <p className="text-white/60 text-sm mb-1">Best Time</p>
            <p className="text-2xl font-bold text-green-400">
              {bestTime ? `${bestTime}ms` : '-'}
            </p>
          </div>
        </div>

        <div className="bg-[#0f0f1e]/80 backdrop-blur-md rounded-3xl p-8 md:p-12 shadow-2xl border border-white/10">
          <div className="grid gap-4 md:gap-6" style={{ gridTemplateColumns: `repeat(${GRID_SIZE}, minmax(0, 1fr))` }}>
            {lights.map(light => (
              <motion.button
                key={light.id}
                data-testid={`light-${light.id}`}
                onPointerDown={() => handleLightTap(light.id)}
                className="aspect-square rounded-full relative overflow-hidden"
                style={{
                  backgroundColor: light.active ? '#00ff88' : '#2a2a3e',
                  boxShadow: light.active ? '0 0 40px #00ff88, inset 0 0 20px #00ff88' : 'inset 0 4px 8px rgba(0,0,0,0.3)',
                  touchAction: 'manipulation'
                }}
                animate={{
                  scale: light.active ? [1, 1.1, 1] : 1
                }}
                transition={{
                  repeat: light.active ? Infinity : 0,
                  duration: 0.5
                }}
                whileTap={{ scale: 0.9 }}
              >
                {light.active && (
                  <div className="absolute inset-0 bg-gradient-radial from-white/30 to-transparent"></div>
                )}
              </motion.button>
            ))}
          </div>
        </div>

        {gameMode === 'endless' && (
          <div className="mt-8 text-center">
            <button
              onClick={backToMenu}
              className="bg-red-500 hover:bg-red-600 text-white px-8 py-3 rounded-full font-bold"
            >
              End Game
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhackALight;
